/**
 * Block 0 Phase 3 (ADR-0013) — переиспользуемая жёлтая CTA для экрана
 * `/install`. Стиль скопирован с `SubscribeCTA` (Phase 1) для визуальной
 * согласованности: token `bg-cta` + `text-ink` + `border-cta-shadow` +
 * `min-h-tap-chip-lg` (56px). Отдельный компонент, потому что:
 *
 * - Нужны три состояния (idle / loading / disabled), которые `SubscribeCTA`
 *   не поддерживает (он простой кнопка-ссылка на оплату).
 * - `aria-busy` для P-022 (a11y baseline) — без него screen-reader не
 *   объявит «загружается», когда отображается spinner.
 * - Семантика подписи отличается («Загрузи приложение», не «Оформить
 *   подписку») — выносить в общий примитив с `children: ReactNode` пока
 *   преждевременно (есть ровно два жёлтых CTA в проекте).
 */

export interface InstallButtonProps {
  /** Текст кнопки в idle-состоянии. */
  label: string;
  /** Текст кнопки в loading-состоянии (заменяет label, отображается рядом с spinner). */
  loadingLabel: string;
  /** Loading-состояние: spinner + aria-busy + блокировка кликов. */
  loading?: boolean;
  /** Disabled (например, в fallback-ветке когда prompt unavailable). */
  disabled?: boolean;
  /** Игнорируется когда loading=true (single-shot guard на UI-уровне). */
  onClick: () => void;
}

export function InstallButton({
  label,
  loadingLabel,
  loading = false,
  disabled = false,
  onClick,
}: InstallButtonProps) {
  const isDisabled = disabled || loading;
  const text = loading ? loadingLabel : label;

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={
        'w-full font-bold rounded-xl flex items-center justify-center gap-2 ' +
        'bg-cta text-ink border-b-2 border-cta-shadow min-h-tap-chip-lg ' +
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
        'focus-visible:outline-none transition-opacity active:opacity-90 ' +
        'disabled:opacity-60 disabled:cursor-not-allowed'
      }
      style={{ fontSize: 20, letterSpacing: '0.01em' }}
    >
      {loading ? (
        <span
          aria-hidden="true"
          data-testid="install-button-spinner"
          className="inline-block w-5 h-5 rounded-full border-2 border-ink border-t-transparent animate-spin"
        />
      ) : null}
      <span>{text}</span>
    </button>
  );
}
